import { defineStore } from 'pinia'
import axios from 'axios'
import { jsPDF } from 'jspdf'
import autoTable from "jspdf-autotable";
import { scoreSheetFonts } from "../config/fonts";
import { flashToast } from "@/toast.js";
import { formatScore } from "@/helpers.js";
import logo from '../../images/logo.jpg'
import pSign from '../../images/psign.jpg'
import eSign from '../../images/esign.jpg'

// ─────────────────────────────────────────────────────────────────────────────
// MODULE-LEVEL HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Safely convert any value to a finite number.
 * Returns `null` if the value is null / undefined / empty-string / NaN / Infinity.
 */
function toNum(value) {
    if (value === null || value === undefined || value === '' || value === '-') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

/**
 * Format a score for display inside tables.
 * Returns '-' for any non-finite / absent value.
 * Integers are shown without a decimal point; floats are shown with one.
 */
function fmt(value) {
    const n = toNum(value);
    if (n === null) return '-';
    return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}

/**
 * Add two score values safely.
 * Returns null if EITHER operand is absent/invalid (so we never display "NaN").
 */
function addScores(a, b) {
    const na = toNum(a);
    const nb = toNum(b);
    if (na === null || nb === null) return null;
    return na + nb;
}

// ─────────────────────────────────────────────────────────────────────────────

export const useResultStore = defineStore('result', {
    state: () => ({
        form: { result: null },
        results: {
            classResult: {},
            term: null,
            year: null,
            termEndDate: null,   // term.end_at — used to derive report date & next term begins
        },
        currentPage: 1,
        itemsPerPage: 3,
        isGenerating: false,
        headerInfo: {
            logo: logo,
            schoolNameArabic: "كلية ابن مسعود للتحفيظ القرآن والدراسات الإسلامية باوتشي",
            schoolNameEnglish: "BN MAS'UD COLLEGE FOR TAHFIZ AND ISLAMIC STUDIES",
            schoolAddress: "Saleh Manga Primary School, Gwallaga Bauchi, Bauchi State",
            contact: "bnmasudcollege@gmail.com, 08032981527, 08036928527, 08032607393",
            fonts: {
                arabic: "AeCortoba",
                titleBold: "Lateef-Bold",
                title: "Lateef",
                subjectArabic: "Amiri-Bold"
            },
            colors: {
                primary: [0, 0, 150],
                secondary: [0, 100, 0],
                text: [0, 0, 0]
            },
            titleFontSize: {
                main: 18,
                sub: 15,
                small: 12
            }
        }
    }),

    actions: {

        // ── Date helpers ──────────────────────────────────────────────────────

        /**
         * Format a Date object or ISO string → "1st January 2025"
         */
        formatDate(dateInput) {
            if (!dateInput) return 'N/A';
            const d = new Date(dateInput);
            if (isNaN(d.getTime())) return 'N/A';
            const n = d.getDate();
            const suffix = (n >= 11 && n <= 13) ? 'th'
                : n % 10 === 1 ? 'st'
                : n % 10 === 2 ? 'nd'
                : n % 10 === 3 ? 'rd' : 'th';
            const month = d.toLocaleString('en-GB', { month: 'long' });
            return `${n}${suffix} ${month} ${d.getFullYear()}`;
        },

        /**
         * Add `days` days to a Date and return a new Date.
         */
        addDays(date, days) {
            const d = new Date(date);
            d.setDate(d.getDate() + days);
            return d;
        },

        // ── Header ────────────────────────────────────────────────────────────

        async renderHeader(doc, yStart = 12) {
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 14;
            const header = this.headerInfo;

            let y = yStart;

            const logoBase64 = await this.getImageAsBase64(header.logo);
            if (logoBase64) {
                doc.addImage(logoBase64, "JPEG", margin, y, 19, 19);                       // left
                doc.addImage(logoBase64, "JPEG", pageWidth - margin - 19, y, 19, 19);      // right
            }

            doc.setFont(header.fonts.arabic, "normal");
            doc.setFontSize(15);
            doc.setTextColor(...header.colors.primary);
            doc.text(header.schoolNameArabic, pageWidth / 2, y + 5, { align: "center" });

            doc.setFont(header.fonts.titleBold, "bold");
            doc.setFontSize(16);
            doc.setTextColor(...header.colors.secondary);
            doc.text(header.schoolNameEnglish, pageWidth / 2, y + 12, { align: "center" });

            doc.setFont(header.fonts.title, "normal");
            doc.setFontSize(14);
            doc.setTextColor(...header.colors.text);
            doc.text(header.schoolAddress, pageWidth / 2, y + 18, { align: "center" });

            doc.setFontSize(11);
            doc.text(header.contact, pageWidth / 2, y + 23, { align: "center" });

            return y + 30;
        },

        // ── Data fetching / setting ───────────────────────────────────────────

        async fetchResults() {
            if (!this.form.result) return;
            try {
                const { data } = await axios.get(`/admin/results/${this.form.result}`);
                this.results = {
                    classResult: data.results,
                    term:        data.term,
                    year:        data.year,
                    termEndDate: data.term_end_date ?? null,
                };
                this.currentPage = 1;
            } catch (error) {
                console.error('Failed to fetch results:', error);
            }
        },

        /**
         * Called by the Vue component when Inertia props are available.
         * termEndDate should be the term's end_at value (ISO string or null).
         */
        setResults(classResult, term, year, termEndDate = null) {
            this.results = { classResult, term, year, termEndDate };
            this.currentPage = 1;
        },

        // ── Font loading ──────────────────────────────────────────────────────

        async loadFonts(doc) {
            if (!scoreSheetFonts?.length) return;
            for (const f of scoreSheetFonts) {
                const response = await fetch(f.url);
                const buffer   = await response.arrayBuffer();
                const binary   = new Uint8Array(buffer).reduce((d, b) => d + String.fromCharCode(b), '');
                doc.addFileToVFS(f.fileName, btoa(binary));
                doc.addFont(f.fileName, f.fontName, f.weight);
            }
        },

        goToPage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.currentPage = page;
        },

        // ══════════════════════════════════════════════════════════════════════
        // RESULT SHEET (class-wide tabular PDF)
        // ══════════════════════════════════════════════════════════════════════

        async generateResultSheet(className) {
            this.isGenerating = true;

            try {
                const students = this.results.classResult[className];
                if (!students?.length) {
                    flashToast({ message: `No students data for: ${className}`, status: false });
                    return;
                }

                // ── 1. Filter subjects ─────────────────────────────────────────
                const allSubjects = students[0].subjects;
                const filtered    = allSubjects.filter((subj, si) =>
                    students.some(stu => {
                        const s = stu.subjects[si];
                        return toNum(s?.ca_score) !== null || toNum(s?.exam_score) !== null;
                    })
                );
                const activeSubjects = filtered.length > 0 ? filtered : allSubjects;
                const subjectCount   = activeSubjects.length;

                // ── 2. Tier sizing ─────────────────────────────────────────────
                const isUltra   = subjectCount >= 13;
                const isCompact = subjectCount >= 9 && subjectCount < 13;
                const isNormal  = subjectCount <= 8;
                const mergeScores = isUltra;

                const orientation = subjectCount > 3 ? 'landscape' : 'portrait';
                const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
                await this.loadFonts(doc);

                const pageWidth   = doc.internal.pageSize.getWidth();
                const margin      = isUltra ? 6 : 10;
                const usableWidth = pageWidth - margin * 2;

                // ── 3. Header ──────────────────────────────────────────────────
                let y = await this.renderHeader(doc, margin);

                doc.setFont(this.headerInfo.fonts.titleBold, 'bold');
                doc.setFontSize(isUltra ? 10 : 12);
                doc.text(
                    `${className} ${this.results.term} ${this.results.year} RESULT SHEET`.toUpperCase(),
                    pageWidth / 2, y, { align: 'center' }
                );
                y += isNormal ? 10 : 6;

                // ══════════════════════════════════════════════════════════════
                // PATH A — normal (≤ 8): autoTable with horizontal headers
                // ══════════════════════════════════════════════════════════════
                if (isNormal) {
                    const bodyRows = students.map((stu, index) => {
                        const subjectCells = activeSubjects.flatMap((_, si) => {
                            const s = stu.subjects[si];
                            return [
                                fmt(s?.ca_score),
                                fmt(s?.exam_score),
                            ];
                        });
                        return [
                            index + 1,
                            `${stu.student.name} | ${stu.student.id ?? ''}`,
                            ...subjectCells,
                            fmt(stu.total_score),
                            fmt(stu.average_score),
                            stu.rank ?? '-',
                        ];
                    });

                    autoTable(doc, {
                        startY: y,
                        head: [
                            [
                                { content: 'S/N',                 rowSpan: 2, styles: { fontStyle: 'bold' } },
                                { content: 'Name | Admission No', rowSpan: 2, styles: { fontStyle: 'bold' } },
                                ...activeSubjects.map(s => ({
                                    content: s.arabic || s.subject,
                                    colSpan: 2,
                                    styles: { halign: 'center', font: 'Amiri-Bold' },
                                })),
                                { content: 'Total',    rowSpan: 2, styles: { fontStyle: 'bold' } },
                                { content: 'Average',  rowSpan: 2, styles: { fontStyle: 'bold' } },
                                { content: 'Position', rowSpan: 2, styles: { fontStyle: 'bold' } },
                            ],
                            [
                                ...activeSubjects.flatMap(() => [
                                    { content: 'CA',   styles: { fontStyle: 'bold' } },
                                    { content: 'Exam', styles: { fontStyle: 'bold' } },
                                ]),
                            ],
                        ],
                        body: bodyRows,
                        theme: 'grid',
                        headStyles: {
                            fillColor: [255, 255, 255],
                            textColor: [0, 0, 0],
                            font: this.headerInfo.fonts.titleBold,
                            fontStyle: 'bold',
                            lineColor: [0, 0, 0],
                            lineWidth: 0.1,
                        },
                        styles: {
                            font: this.headerInfo.fonts.title,
                            fontSize: 10,
                            cellPadding: 2,
                            halign: 'center',
                            valign: 'middle',
                            lineColor: [0, 0, 0],
                            lineWidth: 0.1,
                            textColor: [0, 0, 0],
                        },
                        columnStyles: {
                            0: { cellWidth: 12 },
                            1: { halign: 'left', cellWidth: 70 },
                        },
                        tableWidth: 'auto',
                        showHead: 'firstPage',
                        didParseCell: (cell) => {
                            if (cell.row.section === 'head' && cell.pageNumber > 1) {
                                cell.cell.text    = '';
                                cell.cell.colSpan = 1;
                            }
                        },
                        willDrawCell: (cell) => {
                            if (cell.row.section === 'head' && cell.pageNumber > 1) return false;
                        },
                    });

                    const fileName = `${className} ${this.results.term} ${this.results.year} Result Sheet`
                        .toUpperCase().replace(/[^\w/]+/g, '_');

                    doc.save(`${fileName}.PDF`);
                    return;
                }

                // ══════════════════════════════════════════════════════════════
                // PATH B — compact (9-12) or ultra (13+): manual draw with
                //          rotated subject headers, merge for ultra
                // ══════════════════════════════════════════════════════════════

                // ── 4. Column widths ───────────────────────────────────────────
                const snW   = isUltra ? 8  : 10;
                const sumW  = isUltra ? 10 : 12;
                const nameW = isUltra ? 48 : 55;
                const fixedW      = snW + nameW + sumW * 3;
                const subjectColW = Math.max(
                    isUltra ? 8 : 10,
                    (usableWidth - fixedW) / (mergeScores ? subjectCount : subjectCount * 2)
                );

                // ── 5. Font / row sizes ────────────────────────────────────────
                const bodyFontSize   = isUltra ? 6.5 : 8;
                const headFontSize   = isUltra ? 6   : 7;
                const rotatedHeaderH = isUltra ? 28  : 24;
                const subHeaderH     = 5;
                const bodyRowH       = isUltra ? 5.5 : 6;

                // ── 6. Column metadata ─────────────────────────────────────────
                const colMeta = [];
                colMeta.push({ type: 'sn' });
                colMeta.push({ type: 'name' });
                activeSubjects.forEach((_, si) => {
                    if (mergeScores) {
                        colMeta.push({ type: 'merged', subjIdx: si });
                    } else {
                        colMeta.push({ type: 'ca',   subjIdx: si });
                        colMeta.push({ type: 'exam', subjIdx: si });
                    }
                });
                colMeta.push({ type: 'total' });
                colMeta.push({ type: 'avg' });
                colMeta.push({ type: 'pos' });

                const colWidths = colMeta.map(m => {
                    if (m.type === 'sn')   return snW;
                    if (m.type === 'name') return nameW;
                    if (['total', 'avg', 'pos'].includes(m.type)) return sumW;
                    return subjectColW;
                });

                const colX = [];
                let cx = margin;
                colWidths.forEach(w => { colX.push(cx); cx += w; });
                const tableWidth = cx - margin;

                // ── 7. Rotated subject header row ──────────────────────────────
                const headerY = y;
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.2);

                const drawHeaderCell = (xi, label) => {
                    const x = colX[xi], w = colWidths[xi];
                    doc.rect(x, headerY, w, rotatedHeaderH + subHeaderH, 'S');
                    doc.setFont(this.headerInfo.fonts.titleBold, 'bold');
                    doc.setFontSize(headFontSize);
                    doc.setTextColor(0, 0, 0);
                    const textY = headerY + (rotatedHeaderH + subHeaderH) / 2 + headFontSize * 0.35 / 2;
                    doc.text(label, x + w / 2, textY, { align: 'center' });
                };

                drawHeaderCell(0, 'S/N');
                drawHeaderCell(1, 'Name | Admission No');
                const sumLabels = { total: 'Total', avg: 'Average', pos: 'Position' };
                colMeta.forEach((m, ci) => {
                    if (!['total', 'avg', 'pos'].includes(m.type)) return;
                    drawHeaderCell(ci, sumLabels[m.type]);
                });

                colMeta.forEach((m, ci) => {
                    const isFirstOfPair = m.type === 'ca' || m.type === 'merged';
                    if (!isFirstOfPair) return;

                    const subjLabel = activeSubjects[m.subjIdx]?.arabic || activeSubjects[m.subjIdx]?.subject || '';
                    const spanW     = mergeScores ? colWidths[ci] : colWidths[ci] * 2;
                    const spanX     = colX[ci];
                    const cellCx    = spanX + spanW / 2;
                    const cellCy    = headerY + rotatedHeaderH / 2;

                    doc.setLineWidth(0.2);
                    doc.rect(spanX, headerY, spanW, rotatedHeaderH, 'S');

                    const k  = doc.internal.scaleFactor;
                    const pH = doc.internal.pageSize.getHeight();
                    doc.internal.write(
                        `q ` +
                        `1 0 0 1 ${(cellCx * k).toFixed(4)} ${((pH - cellCy) * k).toFixed(4)} cm ` +
                        `0 1 -1 0 0 0 cm ` +
                        `1 0 0 1 ${(-(cellCx * k)).toFixed(4)} ${(-((pH - cellCy) * k)).toFixed(4)} cm`
                    );
                    doc.setFont('Amiri-Bold', 'bold');
                    doc.setFontSize(headFontSize);
                    doc.setTextColor(0, 0, 0);
                    doc.text(subjLabel, cellCx, cellCy, { align: 'center', baseline: 'middle' });
                    doc.internal.write('Q');
                });

                // ── 8. Sub-header: CA / Exam ───────────────────────────────────
                const subY = headerY + rotatedHeaderH;
                if (!mergeScores) {
                    doc.setLineWidth(0.1);
                    doc.rect(margin, subY, tableWidth, subHeaderH, 'S');
                    colMeta.forEach((m, ci) => {
                        if (m.type !== 'ca' && m.type !== 'exam') return;
                        const label = m.type === 'ca' ? 'CA' : 'Exam';
                        doc.setFont(this.headerInfo.fonts.titleBold, 'bold');
                        doc.setFontSize(headFontSize - 1);
                        doc.setTextColor(0, 0, 0);
                        const x = colX[ci], w = colWidths[ci];
                        doc.rect(x, subY, w, subHeaderH, 'S');
                        doc.text(label, x + w / 2, subY + subHeaderH / 2 + (headFontSize - 1) * 0.35 / 2, { align: 'center' });
                    });
                } else {
                    doc.setLineWidth(0.1);
                    doc.line(margin, subY, margin + tableWidth, subY);
                }

                y = subY + (mergeScores ? 0 : subHeaderH);

                // ── 9. Body rows ───────────────────────────────────────────────
                doc.setFont(this.headerInfo.fonts.title, 'normal');
                doc.setFontSize(bodyFontSize);
                doc.setTextColor(0, 0, 0);

                students.forEach((stu, rowIdx) => {
                    doc.setDrawColor(0, 0, 0);
                    doc.setLineWidth(0.1);
                    doc.rect(margin, y, tableWidth, bodyRowH, 'S');

                    colMeta.forEach((m, ci) => {
                        const x = colX[ci], w = colWidths[ci];
                        doc.rect(x, y, w, bodyRowH, 'S');

                        doc.setFont(this.headerInfo.fonts.title, 'normal');
                        doc.setFontSize(bodyFontSize);
                        doc.setTextColor(0, 0, 0);

                        const midY = y + bodyRowH / 2 + bodyFontSize * 0.35 / 2;
                        const subj = stu.subjects[m.subjIdx];

                        if (m.type === 'sn') {
                            doc.text(String(rowIdx + 1), x + w / 2, midY, { align: 'center' });

                        } else if (m.type === 'name') {
                            doc.setFontSize(bodyFontSize - 0.5);
                            doc.text(stu.student.name, x + 1, y + bodyRowH * 0.38, { maxWidth: w - 2 });
                            doc.setFontSize(bodyFontSize - 1.5);
                            doc.setTextColor(80, 80, 80);
                            doc.text(String(stu.student.id ?? ''), x + 1, y + bodyRowH * 0.75);
                            doc.setTextColor(0, 0, 0);

                        } else if (m.type === 'ca') {
                            doc.text(fmt(subj?.ca_score), x + w / 2, midY, { align: 'center' });

                        } else if (m.type === 'exam') {
                            doc.text(fmt(subj?.exam_score), x + w / 2, midY, { align: 'center' });

                        } else if (m.type === 'merged') {
                            const lineH = bodyRowH / 2;
                            doc.setFontSize(bodyFontSize - 1);
                            doc.text(fmt(subj?.ca_score),   x + w / 2, y + lineH * 0.7,  { align: 'center' });
                            doc.text(fmt(subj?.exam_score), x + w / 2, y + lineH * 1.65, { align: 'center' });
                            doc.setLineWidth(0.05);
                            doc.line(x, y + lineH, x + w, y + lineH);

                        } else if (m.type === 'total') {
                            doc.setFont(this.headerInfo.fonts.titleBold, 'bold');
                            doc.text(fmt(stu.total_score), x + w / 2, midY, { align: 'center' });

                        } else if (m.type === 'avg') {
                            doc.text(fmt(stu.average_score), x + w / 2, midY, { align: 'center' });

                        } else if (m.type === 'pos') {
                            doc.text(String(stu.rank ?? '-'), x + w / 2, midY, { align: 'center' });
                        }
                    });

                    y += bodyRowH;

                    // Page break
                    const pageH = doc.internal.pageSize.getHeight();
                    if (y + bodyRowH > pageH - 15) {
                        doc.addPage();
                        y = margin;
                        const repH = subHeaderH + 2;
                        doc.setFillColor(230, 230, 230);
                        doc.rect(margin, y, tableWidth, repH, 'F');
                        doc.setFont(this.headerInfo.fonts.titleBold, 'bold');
                        doc.setFontSize(headFontSize - 1);
                        doc.setTextColor(0, 0, 0);
                        colMeta.forEach((m, ci) => {
                            const x = colX[ci], w = colWidths[ci];
                            let lbl = '';
                            if (m.type === 'sn')          lbl = '#';
                            else if (m.type === 'name')   lbl = 'Name';
                            else if (m.type === 'ca')     lbl = 'CA';
                            else if (m.type === 'exam')   lbl = 'Exam';
                            else if (m.type === 'merged') lbl = activeSubjects[m.subjIdx]?.arabic ?? '';
                            else if (m.type === 'total')  lbl = 'Total';
                            else if (m.type === 'avg')    lbl = 'Avg';
                            else if (m.type === 'pos')    lbl = 'Pos';
                            doc.rect(x, y, w, repH, 'S');
                            if (m.type === 'merged') {
                                doc.setFont('Amiri-Bold', 'bold');
                                doc.setFontSize(headFontSize - 2);
                            }
                            doc.text(lbl, x + w / 2, y + repH / 2 + (headFontSize - 1) * 0.35 / 2, { align: 'center' });
                            doc.setFont(this.headerInfo.fonts.titleBold, 'bold');
                            doc.setFontSize(headFontSize - 1);
                        });
                        y += repH;
                        doc.setFont(this.headerInfo.fonts.title, 'normal');
                        doc.setFontSize(bodyFontSize);
                    }
                });

                const fileName = `${className} ${this.results.term} ${this.results.year} Result Sheet`
                    .toUpperCase().replace(/[^\w/]+/g, '_');

                doc.save(`${fileName}.PDF`);

            } catch (e) {
                console.error(e);
                flashToast({ message: 'Error generating result sheet', status: false });
            } finally {
                this.isGenerating = false;
            }
        },

        // ══════════════════════════════════════════════════════════════════════
        // REPORT CARDS (per-student individual PDF)
        // ══════════════════════════════════════════════════════════════════════

        async generateResult(classRoomName, student = null) {
            const classData = this.results.classResult[classRoomName];
            if (!classData?.length) {
                flashToast({ message: `No result found for: ${classRoomName}`, status: false });
                return;
            }

            const isSingle = student && typeof student === "object" && student.id;
            let studentList = [];

            if (isSingle) {
                const single = classData.find(r => r.student.id === student.id);
                if (!single) {
                    flashToast({ message: `No result found for student: ${student.name}`, status: false });
                    return;
                }
                studentList = [single];
            } else {
                studentList = classData;
            }

            this.isGenerating      = true;
            this.generationProgress = 0;

            try {
                const doc = new jsPDF("p", "mm", "a4");
                await this.loadFonts(doc);
                doc.setFont("Lateef", "normal");

                // ── Dates ──────────────────────────────────────────────────────
                const termEndRaw = this.results.termEndDate;
                const termEndAt  = termEndRaw ? new Date(termEndRaw) : null;

                const reportDate = termEndAt && !isNaN(termEndAt)
                    ? this.formatDate(termEndAt)
                    : this.formatDate(new Date());

                const nextTermBegins = termEndAt && !isNaN(termEndAt)
                    ? this.formatDate(this.addDays(termEndAt, 1))
                    : 'TBA';

                const signingDate = this.formatDate(new Date());   // always today

                const [logoImg, pSignImg, eSignImg] = await Promise.all([
                    this.getImageAsBase64(logo),
                    this.getImageAsBase64(pSign),
                    this.getImageAsBase64(eSign),
                ]);

                const total     = studentList.length;
                const batchSize = isSingle ? 1 : 5;

                for (let i = 0; i < total; i += batchSize) {
                    await new Promise((resolve) => {
                        setTimeout(async () => {
                            const end = Math.min(i + batchSize, total);
                            for (let j = i; j < end; j++) {
                                const record = studentList[j];
                                this.generationProgress = Math.round(((j + 1) / total) * 100);
                                if (j > 0) doc.addPage();
                                await this.addStudentReportToPdf(
                                    doc,
                                    record,
                                    classRoomName,
                                    classData.length,
                                    reportDate,
                                    logoImg,
                                    pSignImg,
                                    eSignImg,
                                    nextTermBegins,
                                    signingDate
                                );
                            }
                            resolve();
                        }, 0);
                    });
                }

                const fileName = isSingle
                    ? `${student.name} ${student.id} ${this.results.term} ${this.results.year} Report Card`
                    : `${classRoomName} ${this.results.term} ${this.results.year} Report Cards`;

                doc.save(fileName.toUpperCase().replace(/[^\w/]+/g, "_") + ".PDF");

            } catch (error) {
                console.error("Error generating report:", error);
            } finally {
                this.isGenerating       = false;
                this.generationProgress = 0;
            }
        },

        /**
         * Render one student's report card page onto `doc`.
         */
        async addStudentReportToPdf(
            doc, record, className, totalStudents,
            reportDate, logoImg, pSignImg, eSignImg,
            nextTermBegins = 'TBA', signingDate = null
        ) {
            const pageWidth  = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin     = 10;

            const signDate = signingDate ?? this.formatDate(new Date());

            // ── School header ──────────────────────────────────────────────────
            let yPos = await this.renderHeader(doc, margin);

            doc.setFont(this.headerInfo.fonts.titleBold, "bold");
            doc.setFontSize(15);
            doc.text('STUDENT REPORT SHEET', pageWidth / 2, yPos + 8, { align: "center" });
            doc.setFont(this.headerInfo.fonts.title, "normal");
            yPos += 15;

            // ── Student info box ───────────────────────────────────────────────
            doc.rect(margin, yPos, pageWidth - margin * 2, 16, 'S');
            doc.setDrawColor(100, 100, 100);
            doc.line(margin,              yPos + 8, pageWidth - margin, yPos + 8);
            doc.line(pageWidth / 3,       yPos,     pageWidth / 3,      yPos + 16);
            doc.line((pageWidth / 3) * 2, yPos,     (pageWidth / 3) * 2, yPos + 16);

            doc.setFontSize(12);
            doc.text(`Name: ${record.student.name}`,                                    margin + 2,               yPos + 5);
            doc.text(`Class: ${className}`,                                              pageWidth / 3 + 2,        yPos + 5);
            doc.text(`Term: ${this.results.term} ${this.results.year}`,          (pageWidth / 3) * 2 + 2,  yPos + 5);
            doc.text(`Admission Number: ${record.student.id ?? 'N/A'}`,                 margin + 2,               yPos + 13);
            doc.text(`Form Master: ${record.form_master ?? 'N/A'}`,                     pageWidth / 3 + 2,        yPos + 13);
            doc.text(`Date: ${reportDate}`,                                              (pageWidth / 3) * 2 + 2,  yPos + 13);
            yPos += 20;

            // ── Subjects table ─────────────────────────────────────────────────
            const tableResult = this.drawSubjectsTable(doc, record, yPos, totalStudents);
            yPos = tableResult.finalY + 10;

            // ── Grade scale ────────────────────────────────────────────────────
            this.drawGradeScaleTable(doc, pageWidth - 60, tableResult.startY, 50);

            // ── Form Master's remark ───────────────────────────────────────────
            const quranSubject = record.subjects.find(subj => {
                const name = (subj.subject ?? '').toLowerCase();
                return name.includes('quran') || (subj.arabic && subj.arabic.includes('القرآن'));
            }) ?? null;

            const comment = this.formMasterComment(
                record.average_score,
                this.results.term,
                { name: className },
                record.next_class ?? null,
                record.section_name ?? '',
                quranSubject
            );

            doc.setFontSize(12);
            doc.text("Form Master's Remark:", margin, yPos);
            const splitComment = doc.splitTextToSize(comment, pageWidth - margin * 2 - 40);
            doc.text(splitComment, margin + 38, yPos);
            yPos += splitComment.length * 5 + 5;

            // ── Next term begins ───────────────────────────────────────────────
            doc.text(`Next Term Begins: ${nextTermBegins}`, margin, yPos);
            yPos += 20;

            // ── Signatures + QR code ───────────────────────────────────────────
            const signatureY  = pageHeight - 22;
            const signImgH    = 15;
            const signImgTop  = signatureY - signImgH - 2;

            // Left: Principal
            const pImgX   = margin + 2;
            const pImgW   = 30;
            const pLineX1 = margin;
            const pLineX2 = margin + 65;

            doc.addImage(pSignImg, 'JPEG', pImgX, signImgTop, pImgW, signImgH);
            doc.setFontSize(7);
            doc.setTextColor(80, 80, 80);
            doc.text(signDate, pImgX + pImgW + 2, signImgTop + signImgH / 2 + 1);
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.3);
            doc.line(pLineX1, signatureY, pLineX2, signatureY);
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.text("Principal's Sign", pLineX1 + (pLineX2 - pLineX1) / 2, signatureY + 5, { align: 'center' });

            // Right: Exam Officer
            const eLineX1 = pageWidth - margin - 65;
            const eLineX2 = pageWidth - margin;
            const eImgW   = 25;
            const eImgX   = eLineX1 + 2;

            doc.addImage(eSignImg, 'JPEG', eImgX, signImgTop, eImgW, signImgH);
            doc.setFontSize(7);
            doc.setTextColor(80, 80, 80);
            doc.text(signDate, eImgX + eImgW + 2, signImgTop + signImgH / 2 + 1);
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.3);
            doc.line(eLineX1, signatureY, eLineX2, signatureY);
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.text("Exam Officer's Sign", eLineX1 + (eLineX2 - eLineX1) / 2, signatureY + 5, { align: 'center' });

            // Centre: QR code
            try {
                const QRCode = (await import('qrcode')).default;
                const qrData = [
                    `Name: ${record.student.name}`,
                    `Adm No: ${record.student.id ?? 'N/A'}`,
                    `Class: ${className}`,
                    `Term: ${this.results.term} / ${this.results.year}`,
                    `Average: ${fmt(record.average_score)}`,
                    `Position: ${record.rank ?? 'N/A'}`,
                ].join('\n');

                const qrSize    = 22;
                const qrX       = (pageWidth - qrSize) / 2;
                const qrY       = signImgTop - 2;
                const qrDataUrl = await QRCode.toDataURL(qrData, {
                    width:  200,
                    margin: 1,
                    color:  { dark: '#000000', light: '#ffffff' },
                });

                doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
                doc.setFontSize(6);
                doc.setTextColor(120, 120, 120);
                doc.text('Scan to verify', pageWidth / 2, qrY + qrSize + 3, { align: 'center' });
            } catch (qrErr) {
                console.warn('QR code skipped:', qrErr);
            }

            doc.setTextColor(0, 0, 0);
        },

        // ══════════════════════════════════════════════════════════════════════
        // SUBJECTS TABLE (used inside report card)
        // ══════════════════════════════════════════════════════════════════════

        drawSubjectsTable(doc, record, startY, totalStudents) {
            const margin     = 10;
            const pageWidth  = doc.internal.pageSize.getWidth();
            const tableWidth = pageWidth - (margin * 2) - 60;
            let yPos = startY;

            const colWidths = {
                sn:      tableWidth * 0.06,
                subject: tableWidth * 0.25,
                ca:      tableWidth * 0.12,
                exam:    tableWidth * 0.12,
                total:   tableWidth * 0.12,
                grade:   tableWidth * 0.13,
                arabic:  tableWidth * 0.20,
            };

            // Header row
            doc.rect(margin, yPos, tableWidth, 8, 'S');
            doc.setDrawColor(100, 100, 100);
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            doc.setFont('Lateef-Bold', 'bold');

            let xPos = margin;
            doc.text('S/N',      xPos + colWidths.sn / 2,      yPos + 5, { align: 'center' }); xPos += colWidths.sn;
            doc.text('Subjects', xPos + colWidths.subject / 2, yPos + 5, { align: 'center' }); xPos += colWidths.subject;
            doc.text('CA',       xPos + colWidths.ca / 2,      yPos + 5, { align: 'center' }); xPos += colWidths.ca;
            doc.text('Exam',     xPos + colWidths.exam / 2,    yPos + 5, { align: 'center' }); xPos += colWidths.exam;
            doc.text('Total',    xPos + colWidths.total / 2,   yPos + 5, { align: 'center' }); xPos += colWidths.total;
            doc.text('Grade',    xPos + colWidths.grade / 2,   yPos + 5, { align: 'center' }); xPos += colWidths.grade;

            doc.setFont('Amiri-Bold', 'bold');
            doc.text('المواد', xPos + colWidths.arabic - 8, yPos + 5, { align: 'right' });
            doc.setFont('Lateef', 'normal');

            // Vertical dividers for header
            xPos = margin;
            [colWidths.sn, colWidths.subject, colWidths.ca, colWidths.exam, colWidths.total, colWidths.grade, colWidths.arabic].forEach(w => {
                doc.line(xPos, yPos, xPos, yPos + 8);
                xPos += w;
            });
            doc.line(xPos, yPos, xPos, yPos + 8);
            doc.line(margin, yPos,     margin + tableWidth, yPos);
            doc.line(margin, yPos + 8, margin + tableWidth, yPos + 8);

            yPos += 8;
            doc.setFont('Lateef', 'normal');
            doc.setFontSize(12);

            // Data rows
            for (let i = 0; i < record.subjects.length; i++) {
                const subject = record.subjects[i];

                // ── Safe numeric extraction ──────────────────────────────────
                const caNum    = toNum(subject.ca_score);
                const examNum  = toNum(subject.exam_score);
                const totalNum = addScores(subject.ca_score, subject.exam_score);

                const caDisplay    = fmt(caNum);
                const examDisplay  = fmt(examNum);
                const totalDisplay = fmt(totalNum);

                // Grade only if BOTH scores are present and total is a valid number
                const gradeDisplay = totalNum !== null
                    ? this.getGrade(totalNum).grade
                    : '-';

                xPos = margin;
                doc.text((i + 1).toString(),  xPos + colWidths.sn / 2,      yPos + 4, { align: 'center' }); xPos += colWidths.sn;
                doc.text(subject.subject ?? '', xPos + colWidths.subject / 2, yPos + 4, { align: 'center' }); xPos += colWidths.subject;
                doc.text(caDisplay,            xPos + colWidths.ca / 2,      yPos + 4, { align: 'center' }); xPos += colWidths.ca;
                doc.text(examDisplay,          xPos + colWidths.exam / 2,    yPos + 4, { align: 'center' }); xPos += colWidths.exam;
                doc.text(totalDisplay,         xPos + colWidths.total / 2,   yPos + 4, { align: 'center' }); xPos += colWidths.total;
                doc.text(gradeDisplay,         xPos + colWidths.grade / 2,   yPos + 4, { align: 'center' }); xPos += colWidths.grade;

                if (subject.arabic) {
                    doc.setFont('Amiri');
                    doc.setFontSize(10);
                    doc.text(subject.arabic, xPos + colWidths.arabic - 8, yPos + 4, { align: 'right' });
                    doc.setFontSize(12);
                    doc.setFont('Lateef', 'normal');
                }

                // Row borders
                xPos = margin;
                [colWidths.sn, colWidths.subject, colWidths.ca, colWidths.exam, colWidths.total, colWidths.grade, colWidths.arabic].forEach(w => {
                    doc.line(xPos, yPos, xPos, yPos + 6);
                    xPos += w;
                });
                doc.line(xPos, yPos, xPos, yPos + 6);
                doc.line(margin, yPos + 6, margin + tableWidth, yPos + 6);

                yPos += 6;
            }

            // Summary footer
            doc.rect(margin, yPos, tableWidth, 12, 'S');
            doc.line(margin, yPos + 6, margin + tableWidth, yPos + 6);
            doc.line(margin + tableWidth / 2, yPos, margin + tableWidth / 2, yPos + 12);

            doc.setFontSize(12);
            doc.text(`Total Marks: ${fmt(record.total_score)}`,                 margin + 5,                  yPos + 4);
            doc.text(`Average: ${fmt(record.average_score)}`,                   margin + tableWidth / 2 + 5, yPos + 4);
            doc.text(`Position: ${record.rank ?? '-'}`,                         margin + 5,                  yPos + 10);
            doc.text(`Out Of: ${totalStudents ?? '-'}`,                         margin + tableWidth / 2 + 5, yPos + 10);
            doc.line(margin, yPos + 12, margin + tableWidth, yPos + 12);

            return { startY, finalY: yPos + 12 };
        },

        // ══════════════════════════════════════════════════════════════════════
        // GRADE SCALE TABLE (right side of report card)
        // ══════════════════════════════════════════════════════════════════════

        drawGradeScaleTable(doc, x, y, width) {
            const height = 6;
            const grades = [
                { range: '70 - 100', remark: 'Excellent', grade: 'A' },
                { range: '60 - 69',  remark: 'Very Good', grade: 'B' },
                { range: '50 - 59',  remark: 'Credit',    grade: 'C' },
                { range: '40 - 49',  remark: 'Pass',      grade: 'D' },
                { range: '0 - 39',   remark: 'Fail',      grade: 'F' },
            ];

            doc.rect(x, y, width, height, 'S');
            doc.setDrawColor(100, 100, 100);
            doc.setFontSize(12);
            doc.setFont('Lateef-Bold', 'bold');
            doc.text('Grade Scale', x + width / 2, y + 4, { align: 'center' });
            doc.setFont('Lateef', 'normal');

            let yPos   = y + height;
            const colW = width / 3;
            grades.forEach(g => {
                doc.line(x,            yPos,          x + width, yPos);
                doc.line(x,            yPos + height, x + width, yPos + height);
                doc.line(x,            yPos,          x,         yPos + height);
                doc.line(x + colW,     yPos,          x + colW,  yPos + height);
                doc.line(x + colW * 2, yPos,          x + colW * 2, yPos + height);
                doc.line(x + width,    yPos,          x + width, yPos + height);

                doc.setFontSize(12);
                doc.text(g.range,  x + colW / 2,            yPos + 4, { align: 'center' });
                doc.text(g.remark, x + colW + colW / 2,     yPos + 4, { align: 'center' });
                doc.text(g.grade,  x + colW * 2 + colW / 2, yPos + 4, { align: 'center' });
                yPos += height;
            });
        },

        // ══════════════════════════════════════════════════════════════════════
        // GRADE HELPER
        // ══════════════════════════════════════════════════════════════════════

        getGrade(score) {
            // Ensure we always work with a real number
            const n = toNum(score);
            if (n === null) return { grade: '-', remark: 'N/A' };
            if (n >= 70) return { grade: 'A', remark: 'Excellent' };
            if (n >= 60) return { grade: 'B', remark: 'Very Good' };
            if (n >= 50) return { grade: 'C', remark: 'Credit' };
            if (n >= 40) return { grade: 'D', remark: 'Pass' };
            if (n >= 0)  return { grade: 'F', remark: 'Fail' };
            return { grade: '-', remark: 'Invalid' };
        },

        // ══════════════════════════════════════════════════════════════════════
        // IMAGE → BASE64
        // ══════════════════════════════════════════════════════════════════════

        async getImageAsBase64(imgUrl) {
            return new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width  = img.width;
                    canvas.height = img.height;
                    canvas.getContext('2d').drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/jpeg'));
                };
                img.onerror = () => resolve(null);   // graceful fallback
                img.src = imgUrl;
            });
        },

        // ══════════════════════════════════════════════════════════════════════
        // FORM MASTER COMMENT
        // ══════════════════════════════════════════════════════════════════════

        formMasterComment(averageScore, term, currentClass, nextClass, sectionName, quranSubject) {
            // Safely coerce average score for grade lookup
            const safeAverage = toNum(averageScore) ?? 0;
            const grade = this.getGrade(safeAverage);

            // ── Quran total: always sum numerically, never concatenate ──────────
            // Both scores come through toNum() so strings like "30" are handled,
            // and missing/null/undefined values produce null (not 0) from toNum()
            // which then causes addScores to return null → quranTotal stays 0 for
            // the threshold comparisons (conservative: no false promotions).
            const caNum   = toNum(quranSubject?.ca_score);
            const examNum = toNum(quranSubject?.exam_score);

            // If the subject exists but BOTH scores are absent treat as 0 (not promoted)
            // If the subject itself is absent, quranTotal is also 0.
            const quranTotal = quranSubject
                ? (caNum ?? 0) + (examNum ?? 0)
                : 0;

            // No Quran subject recorded — fall back to average-based comment
            if (!quranSubject) {
                if (grade.grade === 'A') return 'Excellent performance.';
                if (grade.grade === 'B') return 'Very good performance.';
                if (grade.grade === 'C') return 'Good performance, work harder next term.';
                if (grade.grade === 'D') return 'Fair performance, you need to put in more effort.';
                return 'Poor performance, you need to work much harder.';
            }

            const termIsThird    = String(term ?? '').toLowerCase().includes('third');
            const sectionLower   = String(sectionName ?? '').toLowerCase();
            const currentName    = currentClass?.name ?? '';
            const isTarteel      = sectionLower.includes('tarteel');
            const isCollege      = sectionLower.includes('hadda college');
            const isMemorization = sectionLower.includes('memorization only');

            const nextName = nextClass?.name ?? (isTarteel ? 'Haddah 1' : currentName);

            // ── Third term: promotion decision ─────────────────────────────────
            if (termIsThird) {
                if (isTarteel) {
                    return quranTotal > 50
                        ? `Promoted to ${nextName}`
                        : `Repeat ${currentName}`;
                }
                if (isCollege || isMemorization) {
                    return quranTotal > 45
                        ? `Promoted to ${nextName}`
                        : `Repeat ${currentName}`;
                }
                // Unknown section — grade-based fallback
                return (grade.grade === 'A' || grade.grade === 'B')
                    ? `Promoted to ${nextName}`
                    : `Repeat ${currentName}`;
            }

            // ── First & second term — Quran-score-based comment ────────────────
            const quranGrade = this.getGrade(quranTotal);
            if (quranGrade.grade === 'A' || quranGrade.grade === 'B') return 'Excellent Result.';
            if (quranGrade.grade === 'C') return 'Good Result, but needs more practice.';
            return 'Needs more dedication please.';
        },
    },

    // ══════════════════════════════════════════════════════════════════════════
    // GETTERS
    // ══════════════════════════════════════════════════════════════════════════

    getters: {
        paginatedResults(state) {
            const allResults = Object.entries(state.results.classResult)
                .filter(([_, results]) => results.length > 0);
            const start = (state.currentPage - 1) * state.itemsPerPage;
            return allResults.slice(start, start + state.itemsPerPage);
        },

        totalPages(state) {
            const totalResults = Object.values(state.results.classResult)
                .filter(res => res.length > 0).length;
            return Math.ceil(totalResults / state.itemsPerPage);
        },
    },
});